import { useState, type ReactNode } from 'react';
import { AlertDialog as RAlertDialog, Dialog as RDialog } from 'radix-ui';
import { X } from 'lucide-react';
import { cn } from '../../lib/cn';
import { Alert } from './Alert';
import { Button } from './Button';
import { buttonClasses } from './buttonStyles';

export interface DialogProps {
  open?: boolean;
  onOpenChange?: (open: boolean) => void;
  /** Element that opens the dialog (rendered with asChild — must be a button). */
  trigger?: ReactNode;
  title: ReactNode;
  description?: ReactNode;
  children?: ReactNode;
  /** Buttons row at the bottom. */
  footer?: ReactNode;
  size?: 'sm' | 'md' | 'lg';
  /** Hide the visible title (still announced). */
  titleHidden?: boolean;
}

const sizes = { sm: 'max-w-md', md: 'max-w-xl', lg: 'max-w-3xl' };

const overlayClass = 'st-animate-fade fixed inset-0 z-40 bg-overlay';
const closeClass =
  'inline-flex h-11 w-11 shrink-0 items-center justify-center rounded-lg text-text hover:bg-surface-muted';

/** Modal dialog (focus trapped, Esc closes, focus returns to the trigger). */
export function Dialog({ open, onOpenChange, trigger, title, description, children, footer, size = 'md', titleHidden }: DialogProps) {
  return (
    <RDialog.Root open={open} onOpenChange={onOpenChange}>
      {trigger && <RDialog.Trigger asChild>{trigger}</RDialog.Trigger>}
      <RDialog.Portal>
        <RDialog.Overlay className={overlayClass} />
        <RDialog.Content
          className={cn(
            'st-animate-pop fixed top-1/2 left-1/2 z-50 flex max-h-[90dvh] w-[calc(100vw-1.5rem)] -translate-x-1/2 -translate-y-1/2 flex-col',
            'rounded-[var(--radius-card)] border border-border bg-surface text-text shadow-card',
            sizes[size],
          )}
          {...(description ? {} : { 'aria-describedby': undefined })}
        >
          <div className="flex items-start justify-between gap-3 border-b border-border p-4">
            <RDialog.Title className={cn('text-xl font-bold', titleHidden && 'sr-only')}>{title}</RDialog.Title>
            <RDialog.Close className={closeClass} aria-label="Close">
              <X aria-hidden="true" className="h-6 w-6" />
            </RDialog.Close>
          </div>
          <div className="overflow-y-auto p-4">
            {description && <RDialog.Description className="mb-3 text-muted">{description}</RDialog.Description>}
            {children}
          </div>
          {footer && <div className="flex flex-wrap justify-end gap-2 border-t border-border p-4">{footer}</div>}
        </RDialog.Content>
      </RDialog.Portal>
    </RDialog.Root>
  );
}

/** A button that closes the surrounding Dialog/Drawer (for footers). */
export function DialogClose({ children = 'Cancel' }: { children?: ReactNode }) {
  return (
    <RDialog.Close className={buttonClasses({ variant: 'secondary' })}>
      <span>{children}</span>
    </RDialog.Close>
  );
}

export interface DrawerProps extends Omit<DialogProps, 'size'> {
  /** right = side panel on desktop; bottom = sheet. Default: bottom on phones, right from `sm` up. */
  side?: 'right' | 'bottom' | 'auto';
}

/** Side panel / bottom sheet built on the same accessible dialog. */
export function Drawer({ open, onOpenChange, trigger, title, description, children, footer, side = 'auto', titleHidden }: DrawerProps) {
  const position =
    side === 'right'
      ? 'st-animate-right inset-y-0 right-0 h-dvh w-full max-w-lg'
      : side === 'bottom'
        ? 'st-animate-bottom inset-x-0 bottom-0 max-h-[90dvh] w-full rounded-t-2xl'
        : 'st-animate-bottom inset-x-0 bottom-0 max-h-[90dvh] w-full rounded-t-2xl sm:inset-x-auto sm:inset-y-0 sm:right-0 sm:h-dvh sm:max-h-none sm:max-w-lg sm:rounded-none';
  return (
    <RDialog.Root open={open} onOpenChange={onOpenChange}>
      {trigger && <RDialog.Trigger asChild>{trigger}</RDialog.Trigger>}
      <RDialog.Portal>
        <RDialog.Overlay className={overlayClass} />
        <RDialog.Content
          className={cn('fixed z-50 flex flex-col border border-border bg-surface text-text shadow-card', position)}
          {...(description ? {} : { 'aria-describedby': undefined })}
        >
          <div className="flex items-start justify-between gap-3 border-b border-border p-4">
            <RDialog.Title className={cn('text-xl font-bold', titleHidden && 'sr-only')}>{title}</RDialog.Title>
            <RDialog.Close className={closeClass} aria-label="Close">
              <X aria-hidden="true" className="h-6 w-6" />
            </RDialog.Close>
          </div>
          <div className="flex-1 overflow-y-auto p-4">
            {description && <RDialog.Description className="mb-3 text-muted">{description}</RDialog.Description>}
            {children}
          </div>
          {footer && <div className="pb-safe flex flex-wrap justify-end gap-2 border-t border-border p-4">{footer}</div>}
        </RDialog.Content>
      </RDialog.Portal>
    </RDialog.Root>
  );
}

export interface ConfirmDialogProps {
  open?: boolean;
  onOpenChange?: (open: boolean) => void;
  trigger?: ReactNode;
  title: ReactNode;
  description: ReactNode;
  confirmLabel?: string;
  cancelLabel?: string;
  tone?: 'primary' | 'danger';
  /** May be async: the dialog shows a spinner and stays open until it resolves (closes on success). */
  onConfirm: () => void | Promise<void>;
}

/** "Are you sure?" dialog for irreversible or significant actions. */
export function ConfirmDialog({
  open,
  onOpenChange,
  trigger,
  title,
  description,
  confirmLabel = 'Confirm',
  cancelLabel = 'Cancel',
  tone = 'primary',
  onConfirm,
}: ConfirmDialogProps) {
  const [internalOpen, setInternalOpen] = useState(false);
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const isOpen = open ?? internalOpen;
  const setOpen = (v: boolean) => {
    if (!v) setError(null);
    if (open === undefined) setInternalOpen(v);
    onOpenChange?.(v);
  };
  return (
    <RAlertDialog.Root open={isOpen} onOpenChange={(v) => !busy && setOpen(v)}>
      {trigger && <RAlertDialog.Trigger asChild>{trigger}</RAlertDialog.Trigger>}
      <RAlertDialog.Portal>
        <RAlertDialog.Overlay className={overlayClass} />
        <RAlertDialog.Content
          className={cn(
            'st-animate-pop fixed top-1/2 left-1/2 z-50 w-[calc(100vw-1.5rem)] max-w-md -translate-x-1/2 -translate-y-1/2',
            'rounded-[var(--radius-card)] border border-border bg-surface p-5 text-text shadow-card',
          )}
        >
          <RAlertDialog.Title className="text-xl font-bold">{title}</RAlertDialog.Title>
          <RAlertDialog.Description className="mt-2 text-muted">{description}</RAlertDialog.Description>
          {error && (
            <Alert tone="danger" live="assertive" className="mt-3">
              {error}
            </Alert>
          )}
          <div className="mt-5 flex flex-wrap justify-end gap-2">
            <RAlertDialog.Cancel className={buttonClasses({ variant: 'secondary' })} disabled={busy}>
              <span>{cancelLabel}</span>
            </RAlertDialog.Cancel>
            <Button
              variant={tone === 'danger' ? 'danger' : 'primary'}
              loading={busy}
              onClick={async () => {
                setBusy(true);
                setError(null);
                try {
                  await onConfirm();
                  setBusy(false);
                  setOpen(false);
                } catch (e) {
                  setBusy(false);
                  setError(e instanceof Error && e.message ? e.message : 'Something went wrong. Please try again.');
                }
              }}
            >
              {confirmLabel}
            </Button>
          </div>
        </RAlertDialog.Content>
      </RAlertDialog.Portal>
    </RAlertDialog.Root>
  );
}
